import React, { useEffect, useState } from 'react';

import { prop } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import StickyHeader from 'components/Layouts/Admin/Dashboard/StickyHeader';
import TextArea from 'components/shared/FormAdapters/FormDefault/TextArea';
import GlobalSearchInput from 'components/shared/GlobalSearchInput';
import Modal from 'components/shared/Modal';
import PageHeader from 'components/shared/PageHeader';
import Table from 'components/shared/Table';
import { PENDING_CONTENT } from 'constants/index';
import { getPath, PAGE_DETAILS_PATH } from 'constants/routes';
import useQueryParams from 'hooks/useQueryParams';
import useTheme from 'hooks/useTheme';
import { selectAction } from 'store/admin/pendingContent';
import {
  dataSelector,
  loadingSelector,
  paginationSelector,
  selectedSelector,
} from 'store/admin/pendingContent/selectors';
import { approveBulkThunk, approveThunk, loadDataThunk, rejectThunk } from 'store/admin/pendingContent/thunks';

import Preview from '../shared/Preview';
import getColumns from './columns';
import useModal from './Confirm/hook';
import styles from './index.module.css';

const APPROVE_DEFAULT_VALUE =
  'Congratulations! Your content has been added to Safari Portal. Please be sure to keep your content up to date as agents rely on this information being accurate when selling your property to potential guests.';

const PendingContent = () => {
  const dispatch = useDispatch();
  const [modalData, setModalData] = useState({ isOpen: false });
  const selected = useSelector(selectedSelector);
  const data = useSelector(dataSelector);
  const pagination = useSelector(paginationSelector);
  const loading = useSelector(loadingSelector);
  const history = useHistory();
  const urlParams = useParams();
  useTheme('secondary2');
  const { setQ, q, queryParams, setSort, setPage, setLimit } = useQueryParams({ defaultLimit: 25 });

  useEffect(() => {
    dispatch(loadDataThunk());
  }, [queryParams, dispatch]);

  const [bulkModalElement2, onBulk2] = useModal('bulk2', (data, update) => ({
    title: 'Approved Content',
    hintBottom: 'Add a helpful description for next steps.',
    confirmButtonText: 'Send',
    onConfirm: () => dispatch(approveBulkThunk(data.value)),
    children: (
      <div style={{ marginBottom: '20px' }}>
        <TextArea
          minRows={5}
          placeholder="Description"
          value={data.value}
          onChange={(e) => update({ value: e.target.value })}
        />
      </div>
    ),
  }));

  const [bulkModalElement1, onBulk1] = useModal('bulk1', (data) => ({
    title: 'Are you sure you want to bulk approve this content?',
    confirmButtonText: 'Confirm',
    onConfirm: () => onBulk2(data),
  }));

  const [rejectModalElement, onReject] = useModal('reject', (data, update) => ({
    title: 'Rejected Content',
    hintBottom: 'Add a helpful description for why this content was rejected.',
    confirmButtonText: 'Send',
    onConfirm: () => dispatch(rejectThunk(data.id, data.value)),
    children: (
      <div style={{ marginBottom: '20px' }}>
        <TextArea
          minRows={5}
          placeholder="Description"
          value={data.value}
          onChange={(e) => update({ value: e.target.value })}
        />
      </div>
    ),
  }));

  const [approveModalElement, onApprove] = useModal('approve', (data, update) => ({
    value:
      'Congratulations! Your content has been added to Safari Portal. Please be sure to keep your content up to date as agents rely on this information being accurate when selling your property to potential guests.',
    title: 'Approved Content',
    hintBottom: 'Add a helpful description for next steps.',
    confirmButtonText: 'Send',
    onConfirm: () => dispatch(approveThunk(data.id, data.value)),
    children: (
      <div style={{ marginBottom: '20px' }}>
        <TextArea
          minRows={5}
          placeholder="Description"
          value={data.value}
          onChange={(e) => update({ value: e.target.value })}
        />
      </div>
    ),
  }));

  return (
    <StickyHeader>
      <PageHeader
        title="Pending Content"
        field={
          <GlobalSearchInput
            value={q}
            placeholder="Filter pending content..."
            onChange={setQ}
            onClear={() => setQ('')}
            onSubmit={() => {}}
            isLoading={loading}
          />
        }
        button={
          <PageHeader.Button onClick={() => onBulk1({ value: APPROVE_DEFAULT_VALUE })} disabled={selected.length === 0}>
            Bulk Approve
          </PageHeader.Button>
        }
      />

      <div style={{ overflow: 'auto' }} data-e2e-widget-type="pageContent" data-e2e-widget-id="plainTableViewWidget">
        <Table
          data={data}
          pagination={pagination}
          limit={queryParams.limit}
          columns={getColumns({
            selected,
            params: queryParams,
            onOrder: (orderBy, order) => setSort(orderBy, order),
            onSelect: (id) => dispatch(selectAction(id)),
            onView: (data) => {
              setModalData({ isOpen: true, pageId: data?.id });
            },
            onEdit: (id) =>
              history.push(
                getPath(PAGE_DETAILS_PATH, { pageId: id, accountId: urlParams.accountId, libType: PENDING_CONTENT }),
              ),
            onApprove: (id) => {
              onApprove({
                id,
                value: APPROVE_DEFAULT_VALUE,
              });
            },
            onReject: (id) => {
              onReject({
                id,
                value: '',
              });
            },
          })}
          onLimitChange={(limit) => setLimit(limit)}
          onSelectPage={(page) => setPage(page)}
          getHeaderProps={headerColumnProps}
          getCellProps={cellProps}
          noDataText="No Content Pages Found"
          isLoading={loading}
          isStickyFirstColumn
          withFixSizedColumns
        />
      </div>

      <Modal
        noPadding
        size="lg"
        isOpen={modalData.isOpen}
        toClose={() => setModalData({ isOpen: false })}
        children={<Preview {...modalData} />}
      />

      {approveModalElement}
      {rejectModalElement}
      {bulkModalElement1}
      {bulkModalElement2}
    </StickyHeader>
  );
};

export default PendingContent;

function headerColumnProps(col) {
  return {
    className: prop(`column-${col.id}`, styles),
  };
}

function cellProps(cell) {
  return {
    className: prop(`column-${cell.column.id}`, styles),
  };
}
